import { useAuth } from '@clerk/nextjs';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import * as Customer from '@web/contracts/Customer';
import axios from 'axios';

interface HasSubscription {
  hasSubscription: true;
  hasSubscriptionMetadata: true;
  success: true;
  subscription: Customer.Subscription.Active;
  stripeCustomerId: string | null;
  /**
   * Billable-role member count, only computed for a trial that can convert from plan selection
   * (trialing with no card, or paused); null otherwise. The server floor for a conversion's Stripe
   * quantity — see countBillableSeatsForConversion, which counts joined members plus pending
   * invitations with a billable role — so the Preview's price matches what Checkout will charge.
   */
  billableSeats: number | null;
}

interface NoSubscription {
  hasSubscription: false;
  success: true;
  stripeCustomerId: string | null;
}

interface Error {
  error: string;
  success: false;
}

export type ApiResponse = HasSubscription | NoSubscription | Error;

export interface Params {
  refetchIntervalInMilliSecond: boolean;
  /** `false` observes the shared result without ever fetching it. */
  enabled?: boolean;
}

/**
 * React Query hook to fetch all the available useSubscriptions for the user from stripe
 * @returns ApiResponse | undefined
 */
const useSubscriptions = (
  params: Params = {
    refetchIntervalInMilliSecond: false,
  },
) => {
  const { orgId } = useAuth();
  return useQuery({
    queryKey: [Query.StripeKeys.subscriptions, { orgId }],
    refetchOnWindowFocus: false,
    retry: false,
    // SubscriptionGuard swaps its children for a loader while this is loading. With no data, a
    // refetch on mount resets it to pending, the loader unmounts every observer, and the next
    // mount refetches again: a request per round trip for as long as the call keeps failing.
    retryOnMount: false,
    enabled: !!orgId && (params.enabled ?? true),
    // A failing call stays failed until reload; polling it only floods the API (one org hit 2k+ in 35 min).
    refetchInterval: (query) => (params.refetchIntervalInMilliSecond && query.state.status !== 'error' ? 5000 : false),
    staleTime: Infinity,
    queryFn: async () => {
      const response = await axios.get<ApiResponse>(
        `${process.env.NEXT_PUBLIC_API_APP_ENDPOINT}/customer/${orgId}/subscription/active-plan`,
      );
      return response.data;
    },
  });
};

export { useSubscriptions };
